import { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { useTenant } from '../context/TenantContext.jsx';
import { getThemePreset, astryxNeutralTheme } from './presets.js';

export const AstryxThemeContext = createContext({
  theme: astryxNeutralTheme,
  themeName: 'astryx-neutral',
  presetKey: 'astryx-neutral',
  mode: 'light',
  setMode: () => {},
});

/**
 * Astryx Theme Provider bridging Astryx tokens, StyleX classes, and Tailwind/CSS variables.
 */
export function AstryxThemeProvider({
  children,
  preset = null,
  mode: initialMode = 'light',
}) {
  let tenant = null;
  try {
    const tenantCtx = useTenant();
    tenant = tenantCtx?.tenant;
  } catch {
    // If mounted outside TenantProvider (e.g. isolated test)
  }

  const [mode, setMode] = useState(initialMode);

  // Determine active preset key from prop, tenant settings, or fallback
  const activePresetKey = useMemo(() => {
    if (preset) return preset;
    if (tenant?.settings?.theme_preset) return tenant.settings.theme_preset;
    return 'astryx-neutral';
  }, [preset, tenant?.settings?.theme_preset]);

  const activeTheme = useMemo(() => {
    return getThemePreset(activePresetKey);
  }, [activePresetKey]);

  // Synchronize CSS custom properties when theme or tenant changes
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    root.setAttribute('data-astryx-preset', activePresetKey);
    // Astryx resolves color-scheme through html[data-theme], not the inner scope.
    // Without this, OS dark mode turns headings white on our light shop surfaces.
    if (mode === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', mode);

    // Apply specific preset color bridges if not overridden by custom tenant colors
    if (activePresetKey === 'astryx-chocolate') {
      root.style.setProperty('--accent', '#f59e0b');
      root.style.setProperty('--primary', tenant?.primaryColor || '#78350f');
      root.style.setProperty('--radius', '0.75rem');
    } else if (activePresetKey === 'modern-blue') {
      root.style.setProperty('--accent', '#2694FE');
      root.style.setProperty('--primary', tenant?.primaryColor || '#0064E0');
      root.style.setProperty('--radius', '0.375rem');
    } else if (activePresetKey === 'industrial-dark') {
      root.style.setProperty('--accent', '#fbbf24');
      root.style.setProperty('--primary', tenant?.primaryColor || '#d97706');
      root.style.setProperty('--radius', '0.25rem');
    } else if (activePresetKey === 'astryx-neutral' || activePresetKey === 'neutral') {
      root.style.setProperty('--accent', '#181716');
      root.style.setProperty('--primary', tenant?.primaryColor || '#e3000b');
      root.style.setProperty('--radius', '0.375rem');
    }
  }, [activePresetKey, tenant?.primaryColor, mode]);

  const contextValue = useMemo(() => ({
    theme: activeTheme,
    themeName: activeTheme?.name || activePresetKey,
    presetKey: activePresetKey,
    mode,
    setMode,
  }), [activeTheme, activePresetKey, mode]);

  return (
    <AstryxThemeContext.Provider value={contextValue}>
      <div
        className="astryx-theme-scope min-h-full"
        data-astryx-theme={activeTheme?.name || activePresetKey}
        data-theme={mode === 'system' ? undefined : mode}
      >
        {children}
      </div>
    </AstryxThemeContext.Provider>
  );
}

export function useAstryxTheme() {
  const ctx = useContext(AstryxThemeContext);
  if (!ctx) {
    throw new Error('useAstryxTheme must be used within an AstryxThemeProvider');
  }
  return ctx;
}
